import type { JSX } from 'react';

import { ChevronDown, ChevronRight, Copy, EllipsisVertical, Pencil, Power, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

import { SCORE_TONE_CLASSES, formatScore, scoreTone } from '../../model/run';
import type { AgentTestScenario, AgentTestScope } from '../../model/scenario';

const SECTION_ORDER: AgentTestScope[] = ['global', 'common', 'subProject'];

const SECTION_TITLES: Record<AgentTestScope, string> = {
  global: 'Globales',
  common: 'Inmobiliaria',
  subProject: 'Proyectos',
};

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

interface ScenarioCardActions {
  onPreview: (scenario: AgentTestScenario) => void;
  onEdit: (scenario: AgentTestScenario) => void;
  onToggleEnabled: (scenario: AgentTestScenario) => void;
  onDuplicate: (scenario: AgentTestScenario) => void;
  onDelete: (scenario: AgentTestScenario) => void;
}

interface ScenarioCardProps extends ScenarioCardActions {
  scenario: AgentTestScenario;
  subProjectName: string | null;
  selected: boolean;
  previewed: boolean;
  onToggleSelected: (scenario: AgentTestScenario, selected: boolean) => void;
}

const ScenarioCard = ({
  scenario,
  subProjectName,
  selected,
  previewed,
  onToggleSelected,
  onPreview,
  onEdit,
  onToggleEnabled,
  onDuplicate,
  onDelete,
}: ScenarioCardProps): JSX.Element => (
  <div
    role="button"
    tabIndex={0}
    onClick={() => onPreview(scenario)}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onPreview(scenario);
      }
    }}
    className={cn(
      'flex cursor-pointer items-start gap-3 rounded-xl border bg-bg-surface p-4 text-left transition-colors',
      previewed ? 'border-brand-vivid ring-1 ring-brand-vivid' : 'border-border-subtle hover:border-border-default'
    )}
  >
    <span
      className="mt-0.5"
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <Checkbox
        checked={selected}
        onCheckedChange={(checked) => onToggleSelected(scenario, checked === true)}
        aria-label={`Seleccionar «${scenario.name}»`}
      />
    </span>

    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <div className="flex items-center gap-2">
        <p
          className={cn(
            'm-0 truncate text-sm font-semibold',
            scenario.enabled ? 'text-text-default' : 'text-text-muted'
          )}
          title={scenario.name}
        >
          {scenario.name}
        </p>
        <span
          className={cn(
            'inline-block shrink-0 rounded-full px-2 py-0.5 text-[0.7rem] font-medium',
            scenario.enabled ? 'bg-success/15 text-success' : 'border border-border-subtle text-text-muted'
          )}
        >
          {scenario.enabled ? 'Habilitado' : 'Deshabilitado'}
        </span>
      </div>

      {(scenario.description?.trim() ?? '').length > 0 || scenario.expectedBehavior.trim().length > 0 ? (
        <p className="m-0 line-clamp-2 text-xs text-text-muted">
          {scenario.description?.trim() ? scenario.description : scenario.expectedBehavior}
        </p>
      ) : null}

      <div className="mt-2 flex flex-wrap items-start gap-x-10 gap-y-2">
        {scenario.tag.trim().length > 0 ? (
          <div className="flex flex-col gap-1">
            <span className="text-[0.7rem] text-text-muted">Categoría</span>
            <span className="inline-block w-fit rounded-full border border-border-subtle px-2 py-0.5 text-[0.7rem] text-text-muted">
              {scenario.tag}
            </span>
          </div>
        ) : null}
        {subProjectName !== null ? (
          <div className="flex flex-col gap-1">
            <span className="text-[0.7rem] text-text-muted">Proyecto</span>
            <span className="max-w-[180px] truncate text-xs text-text-default">{subProjectName}</span>
          </div>
        ) : null}
        <div className="flex flex-col gap-1">
          <span className="text-[0.7rem] text-text-muted">Actualizado</span>
          <span className="text-xs text-text-default">{formatDate(scenario.updatedAt)}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[0.7rem] text-text-muted">Score</span>
          {typeof scenario.lastScore === 'number' ? (
            <span
              className={cn('text-lg leading-none font-bold', SCORE_TONE_CLASSES[scoreTone(scenario.lastScore)].text)}
            >
              {formatScore(scenario.lastScore)}
            </span>
          ) : (
            <span className="text-xs text-text-muted">sin runs</span>
          )}
        </div>
      </div>
    </div>

    <span
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="ghost" size="icon-sm" aria-label={`Acciones para «${scenario.name}»`}>
            <EllipsisVertical className="size-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => onEdit(scenario)}>
            <Pencil className="size-4" aria-hidden />
            Editar
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onToggleEnabled(scenario)}>
            <Power className="size-4" aria-hidden />
            {scenario.enabled ? 'Desactivar' : 'Activar'}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onDuplicate(scenario)}>
            <Copy className="size-4" aria-hidden />
            Duplicar
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onSelect={() => onDelete(scenario)}>
            <Trash2 className="size-4" aria-hidden />
            Eliminar
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  </div>
);

interface ScenarioCardsProps extends ScenarioCardActions {
  scenarios: AgentTestScenario[];
  groupBySections: boolean;
  subProjectNames: Record<number, string>;
  selectedIds: ReadonlySet<number>;
  previewedId: number | null;
  onToggleSelected: (scenario: AgentTestScenario, selected: boolean) => void;
  onToggleSection: (scenarios: AgentTestScenario[], selected: boolean) => void;
}

const ScenarioCards = ({
  scenarios,
  groupBySections,
  subProjectNames,
  selectedIds,
  previewedId,
  onToggleSelected,
  onToggleSection,
  ...actions
}: ScenarioCardsProps): JSX.Element => {
  const cardFor = (scenario: AgentTestScenario): JSX.Element => (
    <ScenarioCard
      key={scenario.id}
      scenario={scenario}
      subProjectName={
        scenario.scope === 'subProject' && scenario.subProjectId !== null
          ? subProjectNames[scenario.subProjectId] ?? `#${scenario.subProjectId}`
          : null
      }
      selected={selectedIds.has(scenario.id)}
      previewed={previewedId === scenario.id}
      onToggleSelected={onToggleSelected}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...actions}
    />
  );

  if (!groupBySections) {
    return <div className="flex flex-col gap-2">{scenarios.map(cardFor)}</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      {SECTION_ORDER.map((scope) => {
        const sectionScenarios = scenarios.filter((scenario) => scenario.scope === scope);
        if (sectionScenarios.length === 0) return null;

        const selectedCount = sectionScenarios.filter((scenario) => selectedIds.has(scenario.id)).length;
        const sectionChecked: boolean | 'indeterminate' =
          selectedCount === 0 ? false : selectedCount === sectionScenarios.length ? true : 'indeterminate';

        return (
          <Collapsible key={scope} defaultOpen>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={sectionChecked}
                onCheckedChange={(checked) => onToggleSection(sectionScenarios, checked === true)}
                aria-label={`Seleccionar todos los escenarios de ${SECTION_TITLES[scope]}`}
              />
              <CollapsibleTrigger className="group flex items-center gap-2 rounded-md py-1 pr-2 text-left">
                <ChevronDown className="size-4 text-text-muted group-data-[state=closed]:hidden" aria-hidden />
                <ChevronRight className="size-4 text-text-muted group-data-[state=open]:hidden" aria-hidden />
                <span className="text-sm font-semibold text-text-default">{SECTION_TITLES[scope]}</span>
                <span className="text-xs text-text-muted">
                  {sectionScenarios.length === 1 ? '1 escenario' : `${sectionScenarios.length} escenarios`}
                </span>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent>
              <div className="mt-2 flex flex-col gap-2 pl-6">{sectionScenarios.map(cardFor)}</div>
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
};

export default ScenarioCards;
